/**
 * Wave 29 Chunk 9b — Job position full-page editor (create/edit).
 *
 * Routes:
 *   /recruitment/positions/new
 *   /recruitment/positions/:pid/edit
 */
import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Briefcase, ChevronLeft, Save, Loader2, X, Plus, Sparkles,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import DatePickerInput from "../components/DatePickerInput";
import VoiceTextarea from "../components/VoiceTextarea";
import { useHidroContext, openHidro } from "../components/HidroAssistant";
import { useBranding } from "../context/TenantBrandingContext";

const EMPLOYMENT = [
  { v: "full_time", l: "Full-time" },
  { v: "part_time", l: "Part-time" },
  { v: "contract", l: "Contract" },
  { v: "internship", l: "Internship" },
  { v: "temp", l: "Temporary" },
];

const STATUSES = [
  { v: "draft", l: "Draft" },
  { v: "open", l: "Open" },
  { v: "on_hold", l: "On hold" },
  { v: "filled", l: "Filled" },
  { v: "closed", l: "Closed" },
];

export default function RecruitmentPositionEditor() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const isHr = user && ["owner", "admin"].includes(user.role);
  const { pid } = useParams();
  const nav = useNavigate();
  const isEdit = Boolean(pid);

  const [form, setForm] = useState({
    title: "", summary: "", location: "", department: "",
    employment_type: "full_time",
    required_skills: [], nice_to_haves: [], responsibilities: [],
    years_min: 0, salary_range: "",
    target_start_date: "", status: "open", hidro_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Hidro DNA Contract ───────────────────────────────────────────────
  // Broadcast a live page-context every time the form changes so when the
  // user pops Hidro open (or it auto-opens on a real title), Hidro knows
  // EVERYTHING about this draft: what's filled, what's empty, suggested
  // next questions. Auto-open fires once the user types a real job title
  // (>3 chars) so Hidro greets them mid-flow instead of after the fact.
  const hidroSummary = useMemo(() => {
    if (isEdit) {
      return `Owner is EDITING the position "${form.title || "(untitled)"}" (${form.status || "draft"}). Goal: refine an existing role, not start from scratch.`;
    }
    if (!form.title?.trim()) {
      return "Owner is on a blank new-position form. No title yet.";
    }
    const filled = [
      form.summary && "summary",
      form.responsibilities?.length > 0 && `${form.responsibilities.length} responsibilities`,
      form.required_skills?.length > 0 && `${form.required_skills.length} required skills`,
      form.nice_to_haves?.length > 0 && `${form.nice_to_haves.length} nice-to-haves`,
      form.salary_range && "salary range",
    ].filter(Boolean);
    return `Owner is creating a new role: "${form.title}" (${form.employment_type}, ${form.location || "no location"}, ${form.department || "no dept"}). Already filled: ${filled.join(", ") || "nothing else yet"}.`;
  }, [form, isEdit]);
  useHidroContext({
    surface: isEdit ? "recruitment_position_edit" : "recruitment_position_new",
    summary: hidroSummary,
    formState: form,
    suggestions: [
      "Help me write the role summary",
      "Suggest key responsibilities for this role",
      "What must-have skills should I require?",
      "Do I already have a similar role open?",
      "Suggest a realistic salary band for South Africa",
    ],
    autoOpenWhen: !isEdit && (form.title || "").trim().length > 3,
    kickoff: "I'm creating a new position. Walk me through it — start by helping me sharpen the role summary based on what I've already entered.",
  });

  useEffect(() => {
    if (!user || !isHr || !isEdit) return;
    setBusy(true);
    (async () => {
      try {
        const { data } = await api.get(`/recruitment/positions/${pid}`);
        setForm({
          title: data?.title || "",
          summary: data?.summary || "",
          location: data?.location || "",
          department: data?.department || "",
          employment_type: data?.employment_type || "full_time",
          required_skills: data?.required_skills || [],
          nice_to_haves: data?.nice_to_haves || [],
          responsibilities: data?.responsibilities || [],
          years_min: data?.years_min ?? 0,
          salary_range: data?.salary_range || "",
          target_start_date: data?.target_start_date || "",
          status: data?.status || "open",
          hidro_notes: data?.hidro_notes || "",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load position.");
      } finally { setBusy(false); }
    })();
  }, [user, isHr, isEdit, pid]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) return toast.error("Title is required.");
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        summary: form.summary.trim() || null,
        location: form.location.trim() || null,
        department: form.department.trim() || null,
        employment_type: form.employment_type,
        required_skills: form.required_skills,
        nice_to_haves: form.nice_to_haves,
        responsibilities: form.responsibilities,
        years_min: Number(form.years_min) || 0,
        salary_range: form.salary_range.trim() || null,
        target_start_date: form.target_start_date || null,
        status: form.status,
        hidro_notes: form.hidro_notes.trim() || null,
      };
      let nextId = pid;
      if (isEdit) {
        await api.patch(`/recruitment/positions/${pid}`, body);
      } else {
        const { data } = await api.post("/recruitment/positions", body);
        nextId = data?.id;
      }
      toast.success("Saved");
      nav(nextId ? `/recruitment/positions/${nextId}` : "/recruitment");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  if (!isHr) {
    return <div className="hv-card p-6 text-sm text-[#dde3ec]" data-testid="position-forbidden">HR only.</div>;
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="position-editor-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to={isEdit ? `/recruitment/positions/${pid}` : "/recruitment"} className="hv-btn hv-btn-ghost text-xs" data-testid="position-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#1FB6C1]" />
          {isEdit ? "Edit position" : "New position"}
        </h1>
        <span />
      </div>

      {busy ? (
        <div className="hv-card p-6 text-sm text-[#9aa3b5]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
      ) : (
        <form onSubmit={submit} className="hv-card p-4 sm:p-5 space-y-4" data-testid="position-form">
          <div className="border border-[#1FB6C1]/25 bg-[#1FB6C1]/[0.05] rounded-lg p-3 flex items-start gap-2.5" data-testid="position-hidro-banner">
            <Sparkles className="w-4 h-4 text-[#1FB6C1] mt-0.5 shrink-0" />
            <div className="text-xs text-[#dde3ec] leading-relaxed flex-1">
              <div className="font-semibold text-[#1FB6C1] mb-0.5">{aiName} is co-piloting this</div>
              <div className="text-[#a0aabf]">
                Just type a job title — {aiName} pops up to help you sharpen the role, suggest responsibilities, and warn you if a similar role already exists.
                Use the <Sparkles className="inline w-2.5 h-2.5 text-[#1FB6C1]" /> link on any field to talk to {aiName} about that specific thing, or the mic icon to dictate.
              </div>
            </div>
            <button
              type="button"
              onClick={() => openHidro({ kickoff: `Walk me through creating "${form.title || "a new role"}" from scratch — ask me the questions you need.` })}
              className="hv-btn hv-btn-ghost text-[11px] shrink-0"
              data-testid="position-hidro-open"
            >
              <Sparkles className="w-3 h-3" /> Open {aiName}
            </button>
          </div>

          <Field label="Job title">
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="hv-input text-sm w-full" data-testid="position-title"
              placeholder="e.g. Senior Field Technician" />
          </Field>

          <Field
            label="Role summary"
            actionLabel={`Ask ${aiName} to draft this`}
            onAction={() => openHidro({ kickoff: `Help me write a 2-3 sentence role summary for "${form.title || "this role"}".` })}
          >
            <VoiceTextarea
              value={form.summary}
              onChange={(v) => setForm({ ...form, summary: v })}
              rows={3}
              context="recruitment_role_summary"
              testIdPrefix="position-summary-voice"
              data-testid="position-summary"
              placeholder={`What does this person do day to day? — tap the mic to dictate, or 'Ask ${aiName}' to draft.`}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Location">
              <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="hv-input text-sm w-full" data-testid="position-location" />
            </Field>
            <Field label="Department">
              <input type="text" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}
                className="hv-input text-sm w-full" data-testid="position-department" />
            </Field>
            <Field label="Employment type">
              <select value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                className="hv-input text-sm w-full" data-testid="position-employment-type">
                {EMPLOYMENT.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </Field>
          </div>

          <TagList
            label="Required skills"
            items={form.required_skills}
            onChange={(items) => setForm({ ...form, required_skills: items })}
            testId="position-required"
            placeholder="e.g. PLC, electrical wiring"
          />

          <TagList
            label="Nice-to-haves"
            items={form.nice_to_haves}
            onChange={(items) => setForm({ ...form, nice_to_haves: items })}
            testId="position-nice"
            placeholder="e.g. SCADA, water industry"
          />

          <TagList
            label="Responsibilities"
            items={form.responsibilities}
            onChange={(items) => setForm({ ...form, responsibilities: items })}
            testId="position-resps"
            placeholder="e.g. Service calls within 50km of Nelspruit"
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Minimum years experience">
              <input type="number" min={0} value={form.years_min} onChange={(e) => setForm({ ...form, years_min: e.target.value })}
                className="hv-input text-sm w-full" data-testid="position-years-min" />
            </Field>
            <Field label="Salary range">
              <input type="text" value={form.salary_range} onChange={(e) => setForm({ ...form, salary_range: e.target.value })}
                className="hv-input text-sm w-full" data-testid="position-salary"
                placeholder="e.g. R 20 000 - R 28 000" />
            </Field>
            <Field label="Target start date">
              <DatePickerInput value={form.target_start_date} onChange={(v) => setForm({ ...form, target_start_date: v })} testId="position-start-date" />
            </Field>
          </div>

          <Field label="Status">
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="hv-input text-sm w-full" data-testid="position-status">
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>

          <Field
            label={`${aiName} notes (free-text clarifications)`}
            actionLabel={`Talk to ${aiName}`}
            onAction={() => openHidro({ kickoff: `For the role "${form.title || "this position"}", what extra context should I capture for CV ranking? (Must-have qualifications, hard constraints, dealbreakers.)` })}
          >
            <VoiceTextarea
              value={form.hidro_notes}
              onChange={(v) => setForm({ ...form, hidro_notes: v })}
              rows={4}
              context="recruitment_hidro_notes"
              testIdPrefix="position-hidro-notes-voice"
              data-testid="position-hidro-notes"
              placeholder="Drop in extra context Hidro should consider when ranking CVs (must have own car, comfortable on weekends, etc.). Tap the mic to dictate."
            />
          </Field>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
            <Link to="/recruitment" className="hv-btn hv-btn-ghost text-xs">Cancel</Link>
            <button type="submit" disabled={saving} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="position-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {isEdit ? "Save changes" : "Create position"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({ label, children, actionLabel, onAction }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">{label}</div>
        {actionLabel && onAction && (
          <button
            type="button"
            onClick={onAction}
            data-testid={`field-ask-hidro-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#1FB6C1] hover:text-white transition"
            title="Talk to Hidro about this field"
          >
            <Sparkles className="w-3 h-3" /> {actionLabel}
          </button>
        )}
      </div>
      {children}
    </label>
  );
}

function TagList({ label, items, onChange, testId, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setDraft("");
  };
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="hv-input text-sm flex-1"
          data-testid={`${testId}-input`}
          placeholder={placeholder}
        />
        <button type="button" onClick={add} className="hv-btn hv-btn-ghost text-xs" data-testid={`${testId}-add`}>
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      {(items || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {items.map((it, idx) => (
            <span key={`${it}-${idx}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/5 text-[11px] text-[#dde3ec]">
              {it}
              <button type="button" onClick={() => onChange(items.filter((_, i) => i !== idx))} className="text-[#647087] hover:text-rose-400" data-testid={`${testId}-remove-${idx}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </Field>
  );
}
